import React, { useState, useContext, useEffect } from 'react';
import './styles/Request.css'; // Importing custom CSS for styling
import Header from '../components/Header'; // Importing Header component
import Footer from '../components/Footer'; // Importing Footer component
import { useNavigate, useParams } from 'react-router-dom'; // Importing navigation hooks
import { UserContext } from '../UserContext'; // Importing UserContext to get user information
import { notifyError, notifySuccess, ToastContainer } from '../components/ErrorAlert'; // Import react-toastify for notifications
import { NotifyMessages } from '../components/NotifyMessages'; // Adjust the path as needed
import { IoMdArrowBack } from "react-icons/io";

function ReopenRequest() {
  const { user, token } = useContext(UserContext); // Getting user and token from UserContext
  
  const navigate = useNavigate(); // Hook for navigation
  const { requestId } = useParams(); // Get request ID from URL params

  // Initial form state
  const [formData, setFormData] = useState({
    supportNeeded: '',
    individuals: [{ name: '', type: '', project: '', vertical: '', purpose: '', otherPurpose: '' }],
    source: '',
    destination: '',
    fromDate: '',
    toDate: '',
    batchId: '',
    center: '',
    client: '',
    costCenter: '',
    otherCostCenter:'',
    email: user ? user.email : '',
    userId: user ? user.id : null,
  });

  // Effect to fetch request data if editing an existing request
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch existing request data for editing
        const response = await fetch(`${process.env.REACT_APP_API_URL}/requests/${requestId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        });

        if (!response.ok) {
        throw new Error('Failed to fetch request data');
        }

        const data = await response.json();
        setFormData({
        supportNeeded: data.supportNeeded,
        individuals: data.individuals.map(individual => {
            const isOthers = individual.purpose !== 'Assessment' && individual.purpose !== 'Meeting';
            return {
            name: individual.name || '',
            type: individual.type || '',
            project: individual.project || '',
            vertical: individual.vertical || '',
            purpose: isOthers ? 'Others' : individual.purpose,
            otherPurpose: isOthers ? individual.purpose : '',
            };
        }),
        source: data.source || '',
        destination: data.destination || '',
        fromDate: data.fromDate ? data.fromDate.split('T')[0] : '',
        toDate: data.toDate ? data.toDate.split('T')[0] : '',
        batchId: data.batchId || '',
        center: data.center || '',
        client: data.client || '',
        costCenter: data.costCenter!=='Govt-Central'&&data.costCenter!=='Govt-State'&&data.costCenter!=='Govt-Others' ? 'Others' : data.costCenter,
        otherCostCenter:data.costCenter!=='Govt-Central'&&data.costCenter!=='Govt-State'&&data.costCenter!=='Govt-Others' ? data.costCenter : '',
        email: data.email,
        userId:data.userId,
        });
      } catch (error) {
        console.error('Error fetching request data:', error);
      }
    };
  
    fetchData(); // Call fetchData function
  
  }, [requestId, token, user]);

  // Handle input changes
  const handleInputChange = (e, index = null) => {
    const { name, value } = e.target;
    
    if (name === 'fromDate') {
      // Validate fromDate (should be today or later)
      const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
      if (value < today) {
        notifyError(NotifyMessages.fromDateError,'bottom-center');
        return;
      }
    } else if (name === 'toDate') {
      // Validate toDate (should not be before fromDate)
      if (formData.fromDate && value < formData.fromDate) {
        notifyError(NotifyMessages.toDateError,'bottom-center');
        return;
      }
    }
    
    if (index !== null) {
      const updatedIndividuals = formData.individuals.map((individual, i) => {
        if (i === index) {
          const updatedIndividual = { ...individual, [name]: value };
          if (name === 'purpose' && value === 'Meeting') {
            updatedIndividual.project = 'Office Meeting';
            updatedIndividual.vertical = 'Office Meeting';
          }
          return updatedIndividual;
        }
        return individual;
      });
      setFormData({ ...formData, individuals: updatedIndividuals });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Add a new individual to the individuals array
  const addIndividual = () => {
    setFormData({
      ...formData,
      individuals: [...formData.individuals, { name: '', type: '', project: '', vertical: '', purpose: '', otherPurpose: '' }],
    });
  };

  // Remove an individual from the individuals array
  const removeIndividual = (index) => {
    const updatedIndividuals = formData.individuals.filter((_, i) => i !== index);
    setFormData({ ...formData, individuals: updatedIndividuals });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
    if (formData.fromDate < today) {
        notifyError(NotifyMessages.fromDateError,'bottom-center');
        return; 
    }
    // Validate toDate (should not be before fromDate)
    if (formData.toDate && formData.fromDate && formData.toDate < formData.fromDate) {
        notifyError(NotifyMessages.toDateError,'bottom-center');
        return;
    }
      
    try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/requests/${requestId}`, {
        method:'PUT',
        headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Network response was not ok: ${response.status} - ${errorText}`);
    }

    notifySuccess(NotifyMessages.requestReopenSuccess, 'bottom-center');
    setTimeout(() => {
        navigate('/'); // Navigate to home or appropriate page after success
    }, 2000);
    } catch (error) {
    console.error('There was a problem with the fetch operation:', error);
    }
  };

  // Conditional rendering based on supportNeeded
  const renderFieldsBasedOnSupport = () => {
    switch (formData.supportNeeded) {
      case 'Travel':
        return (
          <>
            <fieldset>
              <legend>Reopen Request:</legend>
              <label>
                Source:
                <input
                  type="text"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label>
                Destination:
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label>
                Date of Journey:
                <input
                  type="date"
                  name="fromDate"
                  value={formData.fromDate}
                  onChange={handleInputChange}
                  required
                />
              </label>
            </fieldset>
          </>
        );
      case 'Accommodation':
        return (
          <>
            <fieldset>
              <legend>Route Details:</legend>
              <label>
                Location:
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label>
                From Date:
                <input
                  type="date"
                  name="fromDate"
                  value={formData.fromDate}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label>
                To Date:
                <input
                  type="date"
                  name="toDate"
                  value={formData.toDate}
                  onChange={handleInputChange}
                  required
                />
              </label>
            </fieldset>
          </>
        );
      case 'Travel + Accommodation':
        return (
          <>
            <fieldset>
              <legend>Route Details:</legend>
              <label>
                Source:
                <input
                  type="text"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label>
                Destination:
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label>
                From Date:
                <input
                  type="date"
                  name="fromDate"
                  value={formData.fromDate}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label>
                To Date:
                <input
                  type="date"
                  name="toDate"
                  value={formData.toDate}
                  onChange={handleInputChange}
                  required
                />
              </label>
            </fieldset>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="Request">
      <Header />
      <ToastContainer />
      <main className="main-content">
         <div className="form-header">
          <div className="back-icon" onClick={() => navigate(-1)}>
            <IoMdArrowBack />
          </div>
          </div>
        <form className="reqForm" onSubmit={handleSubmit}>
          <h1>{requestId ? 'Edit Request' : 'Request Details'}</h1>

          <fieldset>
            <legend>Requested For:</legend>
            <label>
              Support Needed:
              <select
                name="supportNeeded"
                value={formData.supportNeeded}
                onChange={handleInputChange}
                required
              >
                <option value="">Select the support needed</option>
                <option value="Travel">Travel</option>
                <option value="Accommodation">Accommodation</option>
                <option value="Travel + Accommodation">Travel + Accommodation</option>
              </select>
            </label>
            <label>
              Batch ID:
              <input
                type="text"
                name="batchId"
                value={formData.batchId}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Center:
              <input
                type="text"
                name="center"
                value={formData.center}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Client:
              <input
                type="text"
                name="client"
                value={formData.client}
                onChange={handleInputChange}
                required
              />
            </label>
            <label>
              Cost Center:
              <select
                name="costCenter"
                value={formData.costCenter}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Cost-Center</option>
                <option value="Govt-Central">Govt-Central</option>
                <option value="Govt-State">Govt-State</option>
                <option value="Govt-Others">Govt-Others</option>
                <option value="Others">Others</option>
              </select>
            </label>
            {formData.costCenter === 'Others' && (
              <label>
                Mention Cost-Center if selected Others:
                <input
                  type="text"
                  name="otherCostCenter"
                  value={formData.otherCostCenter}
                  onChange={handleInputChange}
                  required
                />
              </label>
            )}
            {formData.individuals.map((individual, index) => (
              <div key={index} className="individual">
                <h3>Individual {index + 1}</h3>
                <label>
                  Individual Name:
                  <input
                    type="text"
                    name="name"
                    value={individual.name}
                    onChange={(e) => handleInputChange(e, index)}
                    required
                  />
                </label>
                <label>
                  Individual Type:
                  <select
                    name="type"
                    value={individual.type}
                    onChange={(e) => handleInputChange(e, index)}
                    required
                  >
                    <option value="">Select Individual type</option>
                    <option value="Assessor">Assessor</option>
                    <option value="Proctor">Proctor</option>
                    <option value="SME">SME</option>
                    <option value="Employee">Employee</option>
                    <option value="Employee_Assessor">Employee-Assessor</option>
                    <option value="Trainer">Trainer</option>
                    <option value="Counselor">Counselor</option>
                    <option value="Intern">Intern</option>
                  </select>
                </label>
                <label>
                  Purpose:
                  <select
                    name="purpose"
                    value={individual.purpose}
                    onChange={(e) => handleInputChange(e, index)}
                    required
                  >
                    <option value="">Select Purpose</option>
                    <option value="Assessment">Assessment</option>
                    <option value="Meeting">Meeting</option>
                    <option value="Others">Others</option>
                  </select>
                </label>
                {individual.purpose === 'Others' && (
                  <label>
                    Mention Purpose if selected Others:
                    <input
                      type="text"
                      name="otherPurpose"
                      value={individual.otherPurpose}
                      onChange={(e) => handleInputChange(e, index)}
                      required
                    />
                  </label>
                )}
                {(individual.purpose === 'Assessment' || individual.purpose === 'Others') && (
                    <>
                      <label>
                        Project:
                        <input
                          type="text"
                          name="project"
                          value={individual.project}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                        />
                      </label>
                      <label>
                        Vertical:
                        <input
                          type="text"
                          name="vertical"
                          value={individual.vertical}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                        />
                      </label>
                    </>
                )}
                <button
                  type="button"
                  onClick={() => removeIndividual(index)}
                  className="delete-button"
                >
                  Delete
                </button>
              </div>
            ))}
            <button type="button" onClick={addIndividual} className="add-button">
              Add Individual
            </button>
          </fieldset>

          {renderFieldsBasedOnSupport()}

          <button type="submit" className='submit-button'>
            Submit
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}

export default ReopenRequest;
